import { useMemo, useState, useEffect } from 'react';

import { useHistory, useLocation } from 'react-router';
import { ReactPaginateProps } from 'react-paginate';
import { useTranslation } from 'react-i18next';
import {
  FiChevronLeft,
  FiClock,
  FiMonitor,
  FiPlay,
  FiRotateCcw,
  FiCalendar,
} from 'react-icons/fi';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import isPast from 'date-fns/isPast';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import { ptBR } from 'date-fns/locale';
import AddToCalendar from 'react-add-to-calendar';
import 'react-add-to-calendar/dist/react-add-to-calendar.css';
import isFuture from 'date-fns/isFuture';
import { enUS } from 'date-fns/locale';

import {
  ScheduleData,
  FullScheduleData,
  RoomDataWithSpeakers,
} from '@common/types/api';
import { MetaTags, Pagination } from '@components';
import { usePagination, useRoomsQuery, useScheduleQuery } from '@hooks';
import { generateArray, formatRoomDisplayData } from '@utils';

import SchedulePanel from '../Lobby/LobbySidebar/SchedulePanel';
import * as S from './styles';
import RoomCard from './RoomCard';

type HandlePagination = ReactPaginateProps['onPageChange'];

const getInitials = (name: string) =>
  name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();

const formatTime = (dateStr: string) => format(parseISO(dateStr), 'HH:mm');

const formatDate = (dateStr: string) =>
  format(parseISO(dateStr), 'dd MMM', { locale: enUS });

const isSessionLive = (session: FullScheduleData) => {
  if (!session.schedule_date || !session.start_time || !session.end_time)
    return false;
  const start = new Date(`${session.schedule_date}T${session.start_time}`);
  const end = new Date(`${session.schedule_date}T${session.end_time}`);
  return isPast(start) && isFuture(end);
};

const getActiveSession = (schedules: ScheduleData[]) =>
  schedules.find((s) => {
    if (!s.schedule_date || !s.start_time || !s.end_time) return false;
    const start = new Date(`${s.schedule_date}T${s.start_time}`);
    const end = new Date(`${s.schedule_date}T${s.end_time}`);
    return isPast(start) && isFuture(end);
  });

const formatScheduleTime = (time: string) => time.substring(0, 5);

const sortLiveFirst = (list: RoomDataWithSpeakers[]) =>
  [...list].sort((a, b) => {
    const aOnline = formatRoomDisplayData(a).isOnline ? 0 : 1;
    const bOnline = formatRoomDisplayData(b).isOnline ? 0 : 1;
    return aOnline - bOnline;
  });

// ── Progress helper ──────────────────────────────────────

const getSessionProgress = (session: ScheduleData): number => {
  if (!session.schedule_date || !session.start_time || !session.end_time)
    return 0;
  const start = new Date(`${session.schedule_date}T${session.start_time}`);
  const end = new Date(`${session.schedule_date}T${session.end_time}`);
  const now = new Date();
  const total = end.getTime() - start.getTime();
  if (total <= 0) return 0;
  const elapsed = now.getTime() - start.getTime();
  return (elapsed / total) * 100;
};

// ── Live Hero sub-components ─────────────────────────────

const useSessionProgress = (session: ScheduleData | FullScheduleData) => {
  const [val, setVal] = useState(getSessionProgress(session));
  useEffect(() => {
    setVal(getSessionProgress(session));
    const t = setInterval(() => setVal(getSessionProgress(session)), 10000);
    return () => clearInterval(t);
  }, [session]);
  return val;
};

const EQUALIZER_DELAYS = [0, 0.2, 0.4, 0.15];

const LiveEqualizer = () => (
  <S.EqualizerBars>
    {EQUALIZER_DELAYS.map((delay) => (
      <S.EqualizerBar key={`eq-${delay}`} $delay={delay} />
    ))}
  </S.EqualizerBars>
);

const LiveSpeakers = ({ session }: { session: ScheduleData }) => {
  const sessionWithSpeakers = session as FullScheduleData;
  const speakers = sessionWithSpeakers.participants?.data?.slice(0, 3) || [];
  if (speakers.length === 0) return null;

  const names = speakers.map((s) => s.name.split(' ')[0]).join(', ');

  return (
    <S.LiveSpeakerRow>
      <S.LiveSpeakerAvatars>
        {speakers.map((speaker, i) =>
          speaker.photo ? (
            <S.LiveSpeakerAvatar
              key={speaker.id}
              src={speaker.photo}
              alt={speaker.name}
              $index={i}
            />
          ) : (
            <S.LiveSpeakerFallback key={speaker.id} $index={i}>
              {getInitials(speaker.name)}
            </S.LiveSpeakerFallback>
          ),
        )}
      </S.LiveSpeakerAvatars>
      <S.LiveSpeakerNames>{names}</S.LiveSpeakerNames>
    </S.LiveSpeakerRow>
  );
};

const LiveSessionInfo = ({ session }: { session: ScheduleData }) => {
  const progress = useSessionProgress(session);

  return (
    <>
      <S.LiveNowPlaying>
        <S.LiveNowPlayingHeader>
          <LiveEqualizer />
          <S.LiveNowLabel>ACONTECENDO AGORA</S.LiveNowLabel>
        </S.LiveNowPlayingHeader>
        <S.LiveSessionTitle>{session.title}</S.LiveSessionTitle>
      </S.LiveNowPlaying>

      <LiveSpeakers session={session} />

      <S.LiveProgressWrapper>
        <S.LiveProgressTime>
          {formatScheduleTime(session.start_time)}
        </S.LiveProgressTime>
        <S.LiveProgressTrack>
          <S.LiveProgressFill $percent={progress} />
        </S.LiveProgressTrack>
        <S.LiveProgressTime>
          {formatScheduleTime(session.end_time)}
        </S.LiveProgressTime>
      </S.LiveProgressWrapper>
    </>
  );
};

const SidebarLiveCard = ({ room }: { room: RoomDataWithSpeakers }) => {
  const [activeSession, setActiveSession] = useState(() =>
    getActiveSession(room.schedules?.data || []),
  );

  useEffect(() => {
    const check = () =>
      setActiveSession(getActiveSession(room.schedules?.data || []));
    const timer = setInterval(check, 30000);
    return () => clearInterval(timer);
  }, [room.schedules?.data]);

  return (
    <>
      <S.LiveGlassCard>
        <S.LiveRoomName>
          <FiMonitor size={14} />
          {room.name}
        </S.LiveRoomName>

        {activeSession && <LiveSessionInfo session={activeSession} />}
      </S.LiveGlassCard>

      <S.LiveEnterButton to={`/broadcast/${room.slug}`}>
        <FiPlay size={14} />
        Entrar na sala
      </S.LiveEnterButton>
    </>
  );
};

// ── Live section with carousel ───────────────────────────

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SidebarLiveSection = ({ rooms }: { rooms: RoomDataWithSpeakers[] }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (rooms.length <= 1) return undefined;
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % rooms.length);
    }, 8000);
    return () => clearInterval(interval);
  }, [rooms.length]);

  const safeIndex = activeIndex < rooms.length ? activeIndex : 0;
  const currentRoom = rooms[safeIndex];

  return (
    <S.SidebarSection>
      <S.LiveHeroWrapper>
        <S.LiveBadge>
          <S.LiveBadgeDot />
          AO VIVO
        </S.LiveBadge>

        <SidebarLiveCard room={currentRoom} />

        {rooms.length > 1 && (
          <S.LiveCarouselDots>
            {rooms.map((r, idx) => (
              <S.LiveCarouselDot
                key={`dot-${r.id}`}
                $active={idx === safeIndex}
                onClick={() => setActiveIndex(idx)}
              />
            ))}
          </S.LiveCarouselDots>
        )}
      </S.LiveHeroWrapper>
    </S.SidebarSection>
  );
};

// ── Upcoming card sub-component ──────────────────────────

const UpcomingCard = ({ room }: { room: RoomDataWithSpeakers }) => {
  const display = formatRoomDisplayData(room);
  const scheduleCount = room.schedules?.data?.length || 0;
  const isSoon = display.status === 'pages.rooms.soon';

  return (
    <S.UpcomingCard to={`/broadcast/${room.slug}`}>
      <S.UpcomingDateBlock>
        <S.UpcomingDateDay>{display.day}</S.UpcomingDateDay>
        <S.UpcomingDateMonth>{display.month}</S.UpcomingDateMonth>
      </S.UpcomingDateBlock>

      <S.UpcomingInfo>
        <S.UpcomingTitle>{room.name}</S.UpcomingTitle>
        <S.UpcomingMeta>
          <S.UpcomingMetaItem>
            <FiClock size={11} />
            {formatTime(room.start_date)}
          </S.UpcomingMetaItem>
          {scheduleCount > 0 && (
            <S.UpcomingMetaItem>
              <FiMonitor size={11} />
              {scheduleCount} {scheduleCount === 1 ? 'sessão' : 'sessões'}
            </S.UpcomingMetaItem>
          )}
        </S.UpcomingMeta>
      </S.UpcomingInfo>

      <S.UpcomingStatusPill $variant={isSoon ? 'amber' : 'blue'}>
        {isSoon ? 'Em breve' : 'Agendada'}
      </S.UpcomingStatusPill>
    </S.UpcomingCard>
  );
};

// ── Replay row sub-component ─────────────────────────────

const ReplayRow = ({ room }: { room: RoomDataWithSpeakers }) => {
  const scheduleCount = room.schedules?.data?.length || 0;

  return (
    <S.ReplayRow to={`/broadcast/${room.slug}`}>
      <S.ReplayThumb>
        {room.thumb_image && (
          <S.ReplayThumbImage src={room.thumb_image} alt={room.name} />
        )}
        <FiPlay size={14} />
      </S.ReplayThumb>

      <S.ReplayInfo>
        <S.ReplayName>{room.name}</S.ReplayName>
        <S.ReplayMeta>
          <span>{formatDate(room.start_date)}</span>
          {scheduleCount > 0 && (
            <span>
              {scheduleCount} {scheduleCount === 1 ? 'sessão' : 'sessões'}
            </span>
          )}
        </S.ReplayMeta>
      </S.ReplayInfo>
    </S.ReplayRow>
  );
};

// ── No Live (Idle Hero) ─────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SidebarNoLive = ({
  nextRoom,
}: {
  nextRoom: RoomDataWithSpeakers | undefined;
}) => {
  if (!nextRoom) return null;

  const nextSession = nextRoom.schedules?.data?.[0] as
    | FullScheduleData
    | undefined;
  const startDate = parseISO(nextRoom.start_date);
  const countdown = formatDistanceToNow(startDate, {
    locale: ptBR,
    addSuffix: true,
  });

  const calendarEvent = {
    title: nextSession?.title || nextRoom.name,
    description: nextSession?.description || nextRoom.name,
    location: `${window.location.origin}/broadcast/${nextRoom.slug}`,
    startTime: nextSession
      ? `${nextSession.schedule_date}T${nextSession.start_time}-03:00`
      : `${nextRoom.start_date.replace(' ', 'T')}-03:00`,
    endTime: nextSession
      ? `${nextSession.schedule_date}T${nextSession.end_time}-03:00`
      : `${nextRoom.end_date.replace(' ', 'T')}-03:00`,
  };

  return (
    <S.SidebarSection>
      <S.IdleHeroWrapper>
        <S.IdleBadge>
          <S.IdleDot />
          Nenhuma transmissão ao vivo
        </S.IdleBadge>

        <S.NextUpCard>
          <S.NextUpLabel>
            <FiClock size={12} />
            Próxima transmissão
          </S.NextUpLabel>

          <S.NextUpRoomName>{nextRoom.name}</S.NextUpRoomName>

          {nextSession && (
            <S.NextUpSessionTitle>{nextSession.title}</S.NextUpSessionTitle>
          )}

          <S.NextUpDateTime>
            <span>
              <FiCalendar size={12} />
              {format(startDate, 'dd MMM', { locale: ptBR })}
            </span>
            <span>
              <FiClock size={12} />
              {formatTime(nextRoom.start_date)}
            </span>
            <S.NextUpCountdown>· {countdown}</S.NextUpCountdown>
          </S.NextUpDateTime>
        </S.NextUpCard>

        {/* eslint-disable-next-line no-console */}
        {console.log('[SidebarNoLive] calendarEvent:', calendarEvent) as never}
        <S.CalendarWrapper
          onClick={(e) => e.nativeEvent.stopImmediatePropagation()}
        >
          <AddToCalendar
            event={calendarEvent}
            buttonLabel="🔔 Notificar quando iniciar"
            useFontAwesomeIcons={false}
          />
        </S.CalendarWrapper>
      </S.IdleHeroWrapper>
    </S.SidebarSection>
  );
};

// ── Sidebar Live/NoLive (same pattern as /speakers) ─────

const RoomsSidebarLive = ({ session }: { session: FullScheduleData }) => {
  const roomSlug = session.room?.data?.slug;
  const roomName = session.room?.data?.name;
  const speakers = session.participants?.data?.slice(0, 3) ?? [];
  const names = speakers.map((s) => s.name.split(' ')[0]).join(', ');
  const progress = useSessionProgress(session);

  return (
    <S.LiveHeroWrapper style={{ marginBottom: '1rem' }}>
      <S.LiveBadge>
        <S.LiveBadgeDot />
        AO VIVO
      </S.LiveBadge>

      <S.LiveGlassCard>
        {roomName && (
          <S.LiveRoomName>
            <FiMonitor size={14} />
            {roomName}
          </S.LiveRoomName>
        )}

        <S.LiveNowPlaying>
          <S.LiveNowPlayingHeader>
            <S.EqualizerBars>
              {EQUALIZER_DELAYS.map((d) => (
                <S.EqualizerBar key={`eq-${d}`} $delay={d} />
              ))}
            </S.EqualizerBars>
            <S.LiveNowLabel>ACONTECENDO AGORA</S.LiveNowLabel>
          </S.LiveNowPlayingHeader>
          <S.LiveSessionTitle>{session.title}</S.LiveSessionTitle>
        </S.LiveNowPlaying>

        {speakers.length > 0 && (
          <S.LiveSpeakerRow>
            {speakers.map((sp, i) =>
              sp.photo ? (
                <S.LiveSpeakerAvatar
                  key={sp.id}
                  src={sp.photo}
                  alt={sp.name}
                  $index={i}
                />
              ) : (
                <S.LiveSpeakerFallback key={sp.id} $index={i}>
                  {getInitials(sp.name)}
                </S.LiveSpeakerFallback>
              ),
            )}
            <S.LiveSpeakerNames>{names}</S.LiveSpeakerNames>
          </S.LiveSpeakerRow>
        )}

        <S.LiveProgressWrapper>
          <S.LiveProgressTime>
            {formatScheduleTime(session.start_time)}
          </S.LiveProgressTime>
          <S.LiveProgressTrack>
            <S.LiveProgressFill $percent={progress} />
          </S.LiveProgressTrack>
          <S.LiveProgressTime>
            {formatScheduleTime(session.end_time)}
          </S.LiveProgressTime>
        </S.LiveProgressWrapper>
      </S.LiveGlassCard>

      <S.LiveEnterButton to={roomSlug ? `/broadcast/${roomSlug}` : '#'}>
        <FiPlay size={14} />
        Entrar na sala
      </S.LiveEnterButton>
    </S.LiveHeroWrapper>
  );
};

const RoomsNoLiveCard = ({ session }: { session: FullScheduleData }) => {
  const startDate = session.schedule_date
    ? parseISO(`${session.schedule_date}T${session.start_time || '00:00'}`)
    : new Date();
  const countdown = formatDistanceToNow(startDate, {
    locale: ptBR,
    addSuffix: true,
  });

  return (
    <S.IdleHeroWrapper style={{ marginBottom: '1rem' }}>
      <S.IdleBadge>
        <S.IdleDot />
        Nenhuma transmissão ao vivo
      </S.IdleBadge>

      <S.NextUpCard>
        <S.NextUpLabel>
          <FiClock size={12} />
          Próxima transmissão
        </S.NextUpLabel>

        {session.room?.data?.name && (
          <S.NextUpRoomName>{session.room.data.name}</S.NextUpRoomName>
        )}

        <S.NextUpSessionTitle>{session.title}</S.NextUpSessionTitle>

        <S.NextUpDateTime>
          <span>
            <FiCalendar size={12} />
            {session.schedule_date
              ? format(parseISO(session.schedule_date), 'dd MMM', {
                  locale: ptBR,
                })
              : ''}
          </span>
          <span>
            <FiClock size={12} />
            {formatScheduleTime(session.start_time)}
          </span>
          <S.NextUpCountdown>· {countdown}</S.NextUpCountdown>
        </S.NextUpDateTime>
      </S.NextUpCard>

      <S.CalendarWrapper
        onClick={(e) => e.nativeEvent.stopImmediatePropagation()}
      >
        <AddToCalendar
          event={{
            title: session.title,
            description: session.description || '',
            location: session.room?.data?.slug
              ? `${window.location.origin}/broadcast/${session.room.data.slug}`
              : window.location.origin,
            startTime: `${session.schedule_date}T${session.start_time}-03:00`,
            endTime: `${session.schedule_date}T${session.end_time}-03:00`,
          }}
          buttonLabel="🔔 Notificar quando iniciar"
          useFontAwesomeIcons={false}
        />
      </S.CalendarWrapper>
    </S.IdleHeroWrapper>
  );
};

// ── Bottom section carousel (Upcoming + Replays) ────────

const BOTTOM_ROTATE_INTERVAL = 8000;

const SidebarBottomSection = ({
  upcomingRooms,
  replayRooms,
}: {
  upcomingRooms: RoomDataWithSpeakers[];
  replayRooms: RoomDataWithSpeakers[];
}) => {
  const panels: { key: string; content: JSX.Element }[] = [];

  if (upcomingRooms.length > 0) {
    panels.push({
      key: 'upcoming',
      content: (
        <S.SidebarSection>
          <S.SidebarSectionHeader>
            <S.SidebarSectionHeaderLeft>
              <FiClock size={14} color="#fbbf24" />
              <S.SidebarSectionTitle>
                PRÓXIMAS TRANSMISSÕES
              </S.SidebarSectionTitle>
            </S.SidebarSectionHeaderLeft>
          </S.SidebarSectionHeader>
          <S.UpcomingList>
            {upcomingRooms.slice(0, 4).map((room) => (
              <UpcomingCard key={`up-${room.id}`} room={room} />
            ))}
          </S.UpcomingList>
        </S.SidebarSection>
      ),
    });
  }

  if (replayRooms.length > 0) {
    panels.push({
      key: 'replays',
      content: (
        <S.SidebarSection>
          <S.SidebarSectionHeader>
            <S.SidebarSectionHeaderLeft>
              <FiRotateCcw size={14} color="#6c63ff" />
              <S.SidebarSectionTitle>
                REPRISES DISPONÍVEIS
              </S.SidebarSectionTitle>
            </S.SidebarSectionHeaderLeft>
          </S.SidebarSectionHeader>
          <S.ReplayList>
            {replayRooms.slice(0, 5).map((room) => (
              <ReplayRow key={`rp-${room.id}`} room={room} />
            ))}
          </S.ReplayList>
        </S.SidebarSection>
      ),
    });
  }

  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (panels.length <= 1) return undefined;
    const timer = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % panels.length);
    }, BOTTOM_ROTATE_INTERVAL);
    return () => clearInterval(timer);
  }, [panels.length]);

  if (panels.length === 0) return null;

  return (
    <S.SidebarBottomPanel>
      {panels.length > 1 && (
        <S.SidebarCarouselNav>
          {panels.map((p, i) => (
            <S.SidebarCarouselDot
              key={p.key}
              $active={activeIndex === i}
              onClick={() => setActiveIndex(i)}
            />
          ))}
        </S.SidebarCarouselNav>
      )}
      <S.SidebarCarouselTrack>
        {panels.map((p, i) => (
          <S.SidebarCarouselSlide key={p.key} $visible={activeIndex === i}>
            {p.content}
          </S.SidebarCarouselSlide>
        ))}
      </S.SidebarCarouselTrack>
    </S.SidebarBottomPanel>
  );
};

// ── Main page ────────────────────────────────────────────

const Rooms = () => {
  const history = useHistory();
  const { pathname, search } = useLocation();
  const { t } = useTranslation();

  const roomsTitle = t('pages.rooms.title');

  const queryParams = new URLSearchParams(search);
  const currentPage = queryParams.get('page') || '1';

  const roomsQuery = useRoomsQuery(Number(currentPage));

  const pagination = usePagination(
    roomsQuery.data?.meta.pagination.total_pages,
  );

  const rooms = useMemo(
    () => sortLiveFirst(roomsQuery.data?.rooms || []),
    [roomsQuery.data?.rooms],
  );

  const upcomingRooms = useMemo(
    () =>
      rooms.filter(
        (room) => formatRoomDisplayData(room).status === 'pages.rooms.soon',
      ),
    [rooms],
  );

  const replayRooms = useMemo(
    () =>
      rooms.filter(
        (room) => formatRoomDisplayData(room).status === 'pages.rooms.reprise',
      ),
    [rooms],
  );

  const scheduleQuery = useScheduleQuery(1, {});
  const allSchedule = useMemo(
    () => scheduleQuery.data?.schedule ?? [],
    [scheduleQuery.data?.schedule],
  );
  const liveSession = useMemo(
    () => allSchedule.find(isSessionLive),
    [allSchedule],
  );
  const nextSession = useMemo(() => {
    if (liveSession) return undefined;
    const now = new Date();
    return allSchedule.find((s) => {
      if (!s.schedule_date || !s.start_time) return false;
      return new Date(`${s.schedule_date}T${s.start_time}`) > now;
    });
  }, [allSchedule, liveSession]);

  const handlePageNavigation: HandlePagination = ({ selected }) => {
    queryParams.set('page', String(selected + 1));

    history.push({
      pathname,
      search: `?${queryParams.toString()}`,
    });
  };

  return (
    <S.PageWrapper>
      <MetaTags title={roomsTitle} />

      {/* ── Main Content ──────────────────────────────── */}
      <S.MainContent>
        <S.PageHeader>
          <S.PageHeaderLeft>
            <S.BackButton onClick={() => history.goBack()}>
              <FiChevronLeft size={20} />
            </S.BackButton>

            <h1>{roomsTitle}</h1>

            {roomsQuery.data && (
              <S.CountBadge>
                {`${rooms.length} ${roomsTitle.toLowerCase()}`}
              </S.CountBadge>
            )}
          </S.PageHeaderLeft>
        </S.PageHeader>

        <S.RoomGrid>
          {roomsQuery.isLoading
            ? generateArray(6).map((key) => (
                <RoomCard key={`room-skeleton-${key}`} loading />
              ))
            : rooms.map((room) => (
                <RoomCard key={`room-${room.id}`} data={room} />
              ))}
        </S.RoomGrid>

        <Pagination
          pageCount={pagination}
          onPageChange={handlePageNavigation}
        />
      </S.MainContent>

      {/* ── Sidebar ───────────────────────────────────── */}
      <S.Sidebar>
        {/* Section 1: Live or No Live */}
        {liveSession ? (
          <RoomsSidebarLive session={liveSession} />
        ) : (
          nextSession && <RoomsNoLiveCard session={nextSession} />
        )}

        {/* Section 2: Schedule + Upcoming/Replays */}
        <S.ScheduleWrapper>
          <SchedulePanel scheduleQuery={scheduleQuery} />
          <SidebarBottomSection
            upcomingRooms={upcomingRooms}
            replayRooms={replayRooms}
          />
          <S.ScheduleFade />
        </S.ScheduleWrapper>
      </S.Sidebar>
    </S.PageWrapper>
  );
};

export default Rooms;
